import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/forms/Checkbox';
import { Input } from '../../../../components/forms/Input';
import { useToast } from '../../../../contexts/useToast';
import { SchedulingProfile } from '../types';
import { useProfileSave } from '../useProfileSave';
import { buildBookingUrl } from '../utils';

export const BookingPageTab: React.FC<{ profile: SchedulingProfile }> = ({ profile }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { save, isSaving, fieldErrors, formError } = useProfileSave(profile.id);
  const [slug, setSlug] = useState(profile.booking_page_slug ?? '');
  const [enabled, setEnabled] = useState(profile.booking_page_enabled);

  const savedUrl = profile.booking_page_slug ? buildBookingUrl(profile.booking_page_slug) : null;
  const dirty =
    slug !== (profile.booking_page_slug ?? '') || enabled !== profile.booking_page_enabled;

  const handleSave = () => {
    save({ booking_page_slug: slug.trim().toLowerCase() || null, booking_page_enabled: enabled });
  };

  const copyUrl = async () => {
    if (!savedUrl) return;
    await navigator.clipboard.writeText(savedUrl);
    showToast({ type: 'success', message: t('common.copied') });
  };

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {t('schedulingAdmin.bookingPage.intro')}
      </p>

      <Checkbox
        label={t('schedulingAdmin.bookingPage.enabled')}
        helperText={t('schedulingAdmin.bookingPage.enabledHelp')}
        checked={enabled}
        onChange={e => setEnabled(e.target.checked)}
      />

      <Input
        label={t('schedulingAdmin.bookingPage.slug')}
        value={slug}
        onChange={e => setSlug(e.target.value)}
        placeholder="salon-mitte"
        helperText={t('schedulingAdmin.bookingPage.slugHelp')}
        errorText={fieldErrors.booking_page_slug}
        state={fieldErrors.booking_page_slug ? 'error' : 'default'}
        fullWidth
      />

      {savedUrl && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <p className="text-xs uppercase tracking-wide text-gray-500">
            {t('schedulingAdmin.bookingPage.link')}
          </p>
          <p className="text-sm font-mono break-all text-gray-900 dark:text-gray-100">{savedUrl}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={copyUrl}>
              {t('common.copy')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => window.open(savedUrl, '_blank', 'noopener')}
            >
              {t('schedulingAdmin.bookingPage.preview')}
            </Button>
          </div>
          {!profile.booking_page_enabled && (
            <p className="text-xs text-amber-600">
              {t('schedulingAdmin.bookingPage.inactiveHint')}
            </p>
          )}
        </div>
      )}

      {formError && <p className="text-sm text-red-600">{formError}</p>}
      <Button onClick={handleSave} loading={isSaving} disabled={!dirty}>
        {t('common.save')}
      </Button>
    </div>
  );
};
